import { getFinalDownloadUrl } from "../../services/api/viewr";
import type { ViewRInfo } from "../../types";
import type { EditorLocationState, EditorCorrelationContext } from "../../utils/editor";
import { logEditorCorrelation } from "../../utils/editor";

type ResolveProjectDescriptorParams = {
  viewRInfo: ViewRInfo | null;
  locationState: EditorLocationState | null | undefined;
};

export type EditorProjectDescriptor = {
  fileName: string | null;
  viewrGCSPath: string | null;
  objectPath: string | null;
  fallbackDownloadUrl: string | null;
  logicalProjectIdentity: string | null;
  source: "viewRInfo" | "locationState" | "unknown";
};

type ResolveProjectSignedUrlParams = {
  descriptor: EditorProjectDescriptor;
  correlationContext?: EditorCorrelationContext;
  token?: string | null;
  allowFallbackDownloadUrl?: boolean;
};

const getAuthToken = (): string | null =>
  localStorage.getItem("firebaseToken") ||
  sessionStorage.getItem("firebaseToken") ||
  null;

const hasViewrExtension = (value: string | null | undefined): boolean => {
  if (!value) return false;
  return /\.viewr(?:$|[?#])/i.test(value.trim());
};

const pickViewrValue = (
  ...candidates: Array<string | null | undefined>
): string | null => {
  for (const candidate of candidates) {
    if (hasViewrExtension(candidate)) {
      return candidate!.trim();
    }
  }
  return null;
};

const collectViewrValues = (
  ...candidates: Array<string | null | undefined>
): string[] =>
  candidates
    .filter((candidate): candidate is string => hasViewrExtension(candidate))
    .map((candidate) => candidate.trim());

const hasExpectedProjectDownloadDescriptor = (
  descriptor: EditorProjectDescriptor,
): boolean =>
  hasViewrExtension(descriptor.objectPath) ||
  hasViewrExtension(descriptor.viewrGCSPath);

const extractExplicitPathname = (value: string | null | undefined): string | null => {
  if (!value) return null;

  try {
    const url = new URL(value);
    if (url.pathname.includes("/o/")) {
      const encodedObjectPath = url.pathname.split("/o/")[1] ?? "";
      return decodeURIComponent(encodedObjectPath);
    }
    return url.pathname ? decodeURIComponent(url.pathname) : null;
  } catch {
    return value;
  }
};

const extractViewrObjectPath = (value: string | null | undefined): string | null => {
  const explicitPathname = extractExplicitPathname(value);
  if (!explicitPathname) return null;

  const normalized = explicitPathname
    .replace(/^gs:\/\/[^/]+\//, "")
    .replace(/^\/+/, "")
    .trim();

  if (!normalized) {
    return null;
  }

  try {
    return decodeURIComponent(normalized);
  } catch {
    return normalized;
  }
};

const extractViewrBasename = (value: string | null | undefined): string | null => {
  const objectPath = extractViewrObjectPath(value);
  if (!objectPath) return null;
  const lastSegment = objectPath.split("/").filter(Boolean).pop() ?? null;
  if (!lastSegment || !hasViewrExtension(lastSegment)) {
    return null;
  }
  return lastSegment;
};

const normalizeLogicalProjectBasename = (basename: string): string =>
  basename
    .toLowerCase()
    .replace(/ \(\d+\)(?=\.viewr$)/i, "")
    .trim();

export const resolveLogicalProjectIdentity = (
  ...candidates: Array<string | null | undefined>
): string | null => {
  for (const candidate of candidates) {
    const objectPath = extractViewrObjectPath(candidate);
    if (!objectPath || !hasViewrExtension(objectPath)) {
      continue;
    }

    const segments = objectPath.split("/").filter(Boolean);
    if (segments.length === 0) {
      continue;
    }

    const basename = segments[segments.length - 1];
    const directory = segments.slice(0, -1).join("/").toLowerCase();
    if (directory) {
      return directory;
    }

    return normalizeLogicalProjectBasename(basename);
  }

  return null;
};

export const areLogicalProjectIdentitiesEquivalent = (
  left: string | null | undefined,
  right: string | null | undefined,
): boolean => {
  const leftIdentity = resolveLogicalProjectIdentity(left);
  const rightIdentity = resolveLogicalProjectIdentity(right);

  if (!leftIdentity || !rightIdentity) {
    return false;
  }

  return leftIdentity === rightIdentity;
};

const pickCandidateMatchingBasename = (
  candidates: string[],
  basenames: string[],
): string | null => {
  for (const preferredBasename of basenames) {
    for (const candidate of candidates) {
      const basename = extractViewrBasename(candidate);
      if (basename && basename === preferredBasename) {
        return candidate;
      }
    }
  }
  return null;
};

const isCompatibleProjectDownloadUrl = (
  url: string | null | undefined,
  descriptor: EditorProjectDescriptor,
): boolean => {
  if (!url) return false;

  const explicitPathname = extractExplicitPathname(url);
  if (explicitPathname && /\.(png|jpg|jpeg|webp)(?:$|[?#])/i.test(explicitPathname)) {
    return false;
  }

  const descriptorBasename = extractViewrBasename(
    descriptor.objectPath ?? descriptor.viewrGCSPath,
  );
  const urlBasename = extractViewrBasename(url);
  if (
    descriptorBasename &&
    urlBasename &&
    descriptorBasename !== urlBasename
  ) {
    return false;
  }

  if (
    explicitPathname &&
    /\.(viewr|zip|json)(?:$|[?#])/i.test(explicitPathname) === false &&
    /\.(png|jpg|jpeg|webp)(?:$|[?#])/i.test(explicitPathname) === false &&
    /\/o\/[^/]+$/i.test(explicitPathname)
  ) {
    return hasExpectedProjectDownloadDescriptor(descriptor);
  }

  return true;
};

export const resolveEditorProjectDescriptor = ({
  viewRInfo,
  locationState,
}: ResolveProjectDescriptorParams): EditorProjectDescriptor => {
  const rawFileName =
    viewRInfo?.fileName || locationState?.fileName || locationState?.title || null;
  const gcsCandidates = collectViewrValues(
    viewRInfo?.viewrGCSPath,
    viewRInfo?.gcsPath,
    locationState?.viewrGCSPath,
    locationState?.gcsPath,
    locationState?.gcs_path,
  );
  const downloadCandidates = collectViewrValues(
    viewRInfo?.downloadUrl,
    viewRInfo?.convertedDownloadUrl,
    locationState?.downloadUrl,
    locationState?.convertedDownloadUrl,
  );
  const preferredBasenames: string[] = [];
  const appendPreferredBasename = (basename: string | null) => {
    if (!basename || preferredBasenames.includes(basename)) {
      return;
    }
    preferredBasenames.push(basename);
  };
  const rawFileNameBasename = extractViewrBasename(rawFileName);
  for (const candidate of downloadCandidates) {
    appendPreferredBasename(extractViewrBasename(candidate));
  }
  appendPreferredBasename(rawFileNameBasename);

  const viewrGCSPath =
    pickCandidateMatchingBasename(gcsCandidates, preferredBasenames) ??
    gcsCandidates[0] ??
    null;
  const objectPath = viewrGCSPath
    ? viewrGCSPath.replace(/^gs:\/\/[^/]+\//, "")
    : null;
  const canonicalBasename =
    extractViewrBasename(viewrGCSPath) ?? rawFileNameBasename;
  const fallbackDownloadUrl =
    (canonicalBasename
      ? downloadCandidates.find(
          (candidate) => extractViewrBasename(candidate) === canonicalBasename,
        )
      : null) ??
    downloadCandidates[0] ??
    null;
  const logicalProjectIdentity = resolveLogicalProjectIdentity(
    viewrGCSPath,
    fallbackDownloadUrl,
    rawFileName,
  );
  const source = viewrGCSPath
    ? gcsCandidates.includes(viewrGCSPath) &&
      collectViewrValues(viewRInfo?.viewrGCSPath, viewRInfo?.gcsPath).includes(
        viewrGCSPath,
      )
      ? "viewRInfo"
      : gcsCandidates.includes(viewrGCSPath) &&
          collectViewrValues(
            locationState?.viewrGCSPath,
            locationState?.gcsPath,
            locationState?.gcs_path,
          ).includes(viewrGCSPath)
        ? "locationState"
        : "unknown"
    : "unknown";
  const fileName = canonicalBasename ?? rawFileName;

  return {
    fileName,
    viewrGCSPath,
    objectPath,
    fallbackDownloadUrl,
    logicalProjectIdentity,
    source,
  };
};

export const resolveEditorProjectSignedUrl = async ({
  descriptor,
  correlationContext,
  token = getAuthToken(),
  allowFallbackDownloadUrl = true,
}: ResolveProjectSignedUrlParams): Promise<string | null> => {
  if (!descriptor.objectPath) {
    logEditorCorrelation(
      "project_load_descriptor_missing_path",
      correlationContext,
      {
        phase: "resolve_signed_url",
      },
      "warn",
    );
    return descriptor.fallbackDownloadUrl;
  }

  try {
    const response = (await getFinalDownloadUrl(
      descriptor.objectPath,
      token,
    )) as {
      url?: string;
      signed_url?: string;
    } | null;

    const signedUrl =
      typeof response?.signed_url === "string" && response.signed_url.trim()
        ? response.signed_url.trim()
        : typeof response?.url === "string" && response.url.trim()
          ? response.url.trim()
          : null;

    if (
      signedUrl &&
      hasExpectedProjectDownloadDescriptor(descriptor) &&
      isCompatibleProjectDownloadUrl(signedUrl, descriptor)
    ) {
      logEditorCorrelation("project_load_signed_url_resolved", correlationContext, {
        phase: "resolve_signed_url",
        source: descriptor.source,
        objectPath: descriptor.objectPath,
      });
      return signedUrl;
    }

    if (response != null) {
      logEditorCorrelation(
        "project_load_signed_url_missing_expected_field",
        correlationContext,
        {
          phase: "resolve_signed_url",
          source: descriptor.source,
          objectPath: descriptor.objectPath,
          hasSignedUrl:
            typeof response?.signed_url === "string" &&
            response.signed_url.trim().length > 0,
          hasUrl:
            typeof response?.url === "string" &&
            response.url.trim().length > 0,
          signedUrlPreview: signedUrl,
        },
        "warn",
      );
    }
  } catch (error) {
    logEditorCorrelation(
      "project_load_signed_url_resolution_failed",
      correlationContext,
      {
        phase: "resolve_signed_url",
        error: error instanceof Error ? error.message : "unknown_error",
      },
      "warn",
    );
  }

  if (descriptor.fallbackDownloadUrl && allowFallbackDownloadUrl) {
    logEditorCorrelation("project_load_signed_url_fallback_used", correlationContext, {
      phase: "resolve_signed_url",
      source: descriptor.source,
      objectPath: descriptor.objectPath,
    });
  } else if (descriptor.fallbackDownloadUrl) {
    logEditorCorrelation(
      "project_load_signed_url_fallback_suppressed",
      correlationContext,
      {
        phase: "resolve_signed_url",
        source: descriptor.source,
        objectPath: descriptor.objectPath,
      },
      "warn",
    );
  }

  return allowFallbackDownloadUrl ? descriptor.fallbackDownloadUrl : null;
};
